import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { API } from "@/apiConfig";
import "@/App.css";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CustomerProvider } from "./contexts/CustomerContext";
import { RFQProvider } from "./contexts/RFQContext";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import Footer from "./components/Footer";
import DeferredSection from "./components/DeferredSection";
import CookieConsentBanner, {
  getCookieConsentAccepted,
  setCookieConsentAccepted,
} from "./components/CookieConsentBanner";
import TestSiteBanner from "./components/TestSiteBanner";
import RouteSeoSync from "./components/RouteSeoSync";

const GlobalDemandSection = lazy(() => import("./components/GlobalDemandSection"));
const ProductGrid = lazy(() => import("./components/ProductGrid"));
const AboutSection = lazy(() => import("./components/AboutSection"));
const ContactSection = lazy(() => import("./components/ContactSection"));
const AuthPage = lazy(() => import("./components/AuthPage"));
const AccountDashboardPage = lazy(() => import("./components/AccountDashboardPage"));
const BlogPage = lazy(() => import("./components/BlogPage"));
const TestimonialsSection = lazy(() => import("./components/TestimonialsSection"));
const HomeBlogSection = lazy(() => import("./components/HomeBlogSection"));
const ProductDetail = lazy(() => import("./components/ProductDetail"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const RFQPage = lazy(() => import("./components/RFQPage"));
const KvkkPage = lazy(() => import("./components/legal/KvkkPage"));
const GizlilikPolitikasiPage = lazy(() => import("./components/legal/GizlilikPolitikasiPage"));
const CerezPolitikasiPage = lazy(() => import("./components/legal/CerezPolitikasiPage"));
const MarketInsightsBanner = lazy(() => import("./components/MarketInsightsBanner"));
const WorkspaceShowcaseSection = lazy(() => import("./components/workspace-showcase/WorkspaceShowcaseSection"));
const ProcurementModelsSection = lazy(() => import("./components/ProcurementModelsSection"));
const SmartSourcingShowcase = lazy(() => import("./components/SmartSourcingShowcase"));

function RouteFallback({ className = "min-h-[40vh]" }) {
  return <div className={className} aria-hidden />;
}
import { isStaticCatalogOnly, fetchStaticCatalogProducts } from "@/utils/staticCatalog";
import {
  resolveProductIdFromUrlSegment,
  productSlugForId,
  productSlugForIdSync,
  ensureProductSlugIndex,
} from "@/utils/productSlug";
import {
  initManualScrollRestoration,
  scrollPageToTop,
  scrollToHomeSection,
} from "@/utils/scrollPage";

const ANALYTICS_SESSION_KEY = "demax_analytics_session";

/** `/products/baby-diaper` veya legacy `/products/wc-18628` → iç ürün kimliği. */
function parseProductIdFromPathname(pathname) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  if (!p.startsWith("/products/")) return null;
  const seg = p.slice("/products/".length).split("/").filter(Boolean)[0];
  if (!seg) return null;
  let decoded = seg;
  try {
    decoded = decodeURIComponent(seg);
  } catch {
    /* keep seg */
  }
  return resolveProductIdFromUrlSegment(decoded);
}

function getInitialRouteState() {
  if (typeof window === "undefined") {
    return { page: "home", productId: null };
  }
  if (computeAdminEntryVisible()) {
    return { page: pathToPage(window.location.pathname), productId: null };
  }
  const productId = parseProductIdFromPathname(window.location.pathname);
  if (productId) {
    return { page: "product", productId };
  }
  return { page: pathToPage(window.location.pathname), productId: null };
}

// Şimdilik kapalı — üstte sarı test şeridi gösterilmez. Açmak için env ile tekrar bağlayın.
const SHOW_TEST_SITE_NOTICE = false;
// const SHOW_TEST_SITE_NOTICE =
//   process.env.REACT_APP_TEST_SITE_NOTICE === "true" ||
//   process.env.REACT_APP_TEST_SITE_NOTICE === "1";

/**
 * Secret admin UI path (no leading slash), e.g. `demaxtore/xYz9AbC12`.
 * Set `REACT_APP_ADMIN_PATH` in production. In development, unset → `/wp-admin`.
 */
function getAdminEntryPath() {
  const raw = process.env.REACT_APP_ADMIN_PATH;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim().replace(/^\/+/g, "").replace(/\/+$/g, "");
  }
  if (process.env.NODE_ENV === "development") {
    return "wp-admin";
  }
  return "";
}

/** e.g. `https://siteniz.com/?key=...` — build-time secret; boşsa bu yöntem kapalı */
function getAdminQueryKeySecret() {
  const raw = process.env.REACT_APP_ADMIN_QUERY_KEY;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim();
  }
  return "";
}

function pathnameMatchesAdminEntry(pathname) {
  const seg = getAdminEntryPath();
  if (!seg) return false;
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  const prefix = `/${seg}`;
  return p === prefix || p.startsWith(`${prefix}/`);
}

function searchMatchesAdminQueryKey(search) {
  const secret = getAdminQueryKeySecret();
  if (!secret) return false;
  const params = new URLSearchParams(search || "");
  const key = params.get("key");
  return key != null && key !== "" && key === secret;
}

function computeAdminEntryVisible() {
  if (typeof window === "undefined") return false;
  return (
    pathnameMatchesAdminEntry(window.location.pathname) ||
    searchMatchesAdminQueryKey(window.location.search)
  );
}

function pathToPage(pathname) {
  const p = (pathname || "/").replace(/\/$/, "") || "/";
  if (parseProductIdFromPathname(pathname || "/")) return "product";
  if (p === "/kvkk") return "kvkk";
  if (p === "/gizlilik-politikasi") return "gizlilik_politikasi";
  if (p === "/cerez-politikasi") return "cerez_politikasi";
  if (p === "/products") return "products";
  if (p === "/about") return "about";
  if (p === "/contact") return "contact";
  if (p === "/blog") return "blog";
  if (p === "/account/dashboard" || p === "/account/rfqs" || p === "/demaxtore/rfqs") return "account_dashboard";
  if (p === "/account" || p === "/hesap") return "auth";
  if (p === "/rfq/workspace" || p === "/rfq") return "rfq";
  if (p === "/wishlist") return "products";
  return "home";
}

function pageToPath(page, navOptions) {
  const opts = navOptions && typeof navOptions === "object" ? navOptions : {};
  switch (page) {
    case "kvkk":
      return "/kvkk";
    case "gizlilik_politikasi":
      return "/gizlilik-politikasi";
    case "cerez_politikasi":
      return "/cerez-politikasi";
    case "products":
    case "categories":
      return "/products";
    case "about":
      return "/about";
    case "contact":
      return "/contact";
    case "blog":
      if (opts.blogSlug && typeof opts.blogSlug === "string") {
        return `/blog#${encodeURIComponent(opts.blogSlug)}`;
      }
      return "/blog";
    case "account_dashboard":
      return "/account/dashboard";
    case "auth":
      return "/account";
    case "rfq":
      return "/rfq";
    case "product":
      if (opts.productSlug) {
        return `/products/${encodeURIComponent(String(opts.productSlug))}`;
      }
      if (opts.productId) {
        const slug = productSlugForIdSync(opts.productId);
        if (slug) return `/products/${encodeURIComponent(slug)}`;
        return `/products/${encodeURIComponent(String(opts.productId))}`;
      }
      return "/products";
    case "home":
      if (opts.homeSection && typeof opts.homeSection === "string") {
        return `/#${opts.homeSection.replace(/^#/, "")}`;
      }
      return "/";
    default:
      return "/";
  }
}

function getSessionId() {
  const existing = localStorage.getItem(ANALYTICS_SESSION_KEY);
  if (existing) return existing;
  const created = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(ANALYTICS_SESSION_KEY, created);
  return created;
}

function getDeviceType() {
  const ua = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|mobile/.test(ua) ? "mobile" : "desktop";
}

function isRfqWorkspaceEmbed() {
  if (typeof window === 'undefined') return false;
  const path = (window.location.pathname || '').replace(/\/$/, '') || '/';
  if (path === '/rfq/workspace') return true;
  if (new URLSearchParams(window.location.search).get('embed') === 'workspace') return true;
  try {
    return sessionStorage.getItem('demax_rfq_embed_workspace') === '1';
  } catch {
    return false;
  }
}

function detectSource(referrer) {
  const params = new URLSearchParams(window.location.search);
  const utmSource = (params.get("utm_source") || "").toLowerCase();
  if (utmSource) return utmSource;
  const r = (referrer || "").toLowerCase();
  if (!r) return "direct";
  if (r.includes("google.")) return "google";
  if (["facebook.", "instagram.", "t.co", "twitter.", "linkedin.", "youtube."].some((k) => r.includes(k))) {
    return "social";
  }
  return "referral";
}

function App() {
  const [isAdminRoute, setIsAdminRoute] = useState(() => computeAdminEntryVisible());
  const initialRoute = useMemo(
    () => (typeof window !== "undefined" ? getInitialRouteState() : { page: "home", productId: null }),
    [],
  );
  const [currentPage, setCurrentPage] = useState(initialRoute.page);
  const rfqWorkspaceEmbed = useMemo(() => isRfqWorkspaceEmbed(), []);

  useEffect(() => {
    if (!rfqWorkspaceEmbed || typeof document === 'undefined') return;
    try {
      sessionStorage.setItem('demax_rfq_embed_workspace', '1');
    } catch {
      /* ignore */
    }
    document.documentElement.setAttribute('data-rfq-embed', 'workspace');
    document.body.classList.add('rfq-embed-workspace');
  }, [rfqWorkspaceEmbed]);
  const [selectedProductId, setSelectedProductId] = useState(initialRoute.productId);
  const [cookieConsent, setCookieConsent] = useState(() => getCookieConsentAccepted());
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const lastPageRef = useRef("home");
  const pageStartRef = useRef(Date.now());

  const pushProductUrl = useCallback((productId) => {
    if (typeof window === "undefined") return;
    const syncSlug = productSlugForIdSync(productId);
    const path = syncSlug
      ? pageToPath("product", { productSlug: syncSlug })
      : pageToPath("product", { productId });
    window.history.pushState({ page: "product", productId }, "", path);
    if (!syncSlug) {
      void productSlugForId(productId).then((slug) => {
        if (!slug || slug === syncSlug) return;
        window.history.replaceState(
          { page: "product", productId },
          "",
          pageToPath("product", { productSlug: slug }),
        );
      });
    }
  }, []);

  const handleNavigate = useCallback((page, productId, navOptions) => {
    const opts = navOptions && typeof navOptions === "object" ? navOptions : {};
    const homeSection =
      typeof opts.homeSection === "string" ? opts.homeSection.replace(/^#/, "") : "";

    if (page === 'product' && productId) {
      scrollPageToTop();
      setSelectedProductId(productId);
      setCurrentPage('product');
      pushProductUrl(productId);
    } else {
      if (!(page === "home" && homeSection)) {
        scrollPageToTop();
      }
      setCurrentPage(page);
      const preserveFilters = opts.preserveFilters === true;
      if (!preserveFilters) {
        setSearchQuery('');
        setCategoryFilter('');
        setSubcategoryFilter('');
      }
      setSelectedProductId(null);
      if (typeof window !== "undefined") {
        window.history.pushState(
          homeSection ? { page, homeSection } : { page },
          "",
          pageToPath(page, opts),
        );
        if (page === "home" && homeSection) {
          scrollToHomeSection(homeSection);
        }
      }
    }
  }, [pushProductUrl]);

  useEffect(() => {
    if (typeof window === "undefined" || currentPage !== "home") return;
    const hash = (window.location.hash || "").replace(/^#/, "").trim();
    if (hash) scrollToHomeSection(hash);
  }, [currentPage]);

  useEffect(() => {
    const onPop = () => {
      setIsAdminRoute(computeAdminEntryVisible());
      const pathname = window.location.pathname;
      const pid = parseProductIdFromPathname(pathname);
      if (pid) {
        setCurrentPage("product");
        setSelectedProductId(pid);
        return;
      }
      setSelectedProductId(null);
      setCurrentPage(pathToPage(pathname));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /** Production: hide legacy `/wp-admin` when a secret path is configured. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const secret = getAdminEntryPath();
    if (!secret || secret === "wp-admin") return;
    const p = window.location.pathname;
    if (p === "/wp-admin" || p.startsWith("/wp-admin/")) {
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const acceptCookies = useCallback(() => {
    setCookieConsentAccepted();
    setCookieConsent(true);
  }, []);

  const handleSearch = useCallback((query) => {
    scrollPageToTop();
    setSearchQuery(query);
    setCategoryFilter('');
    setSubcategoryFilter('');
    setCurrentPage('products');
    setSelectedProductId(null);
    if (typeof window !== "undefined") {
      window.history.pushState({ page: "products" }, "", pageToPath("products"));
    }
  }, []);

  const handleCategorySelect = useCallback((cat) => {
    scrollPageToTop();
    if (cat && typeof cat === 'object') {
      setCategoryFilter(cat.category || '');
      setSubcategoryFilter(cat.subcategory || '');
      setSearchQuery(cat.search || '');
    } else {
      setCategoryFilter(cat || '');
      setSubcategoryFilter('');
      setSearchQuery('');
    }
    setCurrentPage('products');
    setSelectedProductId(null);
    if (typeof window !== "undefined") {
      window.history.pushState({ page: "products" }, "", pageToPath("products"));
    }
  }, []);

  const handleProductClick = useCallback((productId) => {
    scrollPageToTop();
    setSelectedProductId(productId);
    setCurrentPage('product');
    pushProductUrl(productId);
  }, [pushProductUrl]);

  /** Statik katalog JSON: ana sayfada arka planda indir; Ürünler’e tıklanınca ağ beklemesi kısalır. */
  const warmStaticCatalog = useCallback(() => {
    if (!isStaticCatalogOnly()) return;
    fetchStaticCatalogProducts()
      .then(() => ensureProductSlugIndex())
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdminRoute || typeof window === "undefined") return undefined;
    let cancelled = false;
    const run = () => {
      if (!cancelled) void ensureProductSlugIndex().catch(() => {});
    };
    const w = window;
    const id =
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(run, { timeout: 5000 })
        : w.setTimeout(run, 2000);
    return () => {
      cancelled = true;
      if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(id);
      else w.clearTimeout(id);
    };
  }, [isAdminRoute]);

  useEffect(() => {
    if (isAdminRoute || !isStaticCatalogOnly()) return undefined;
    // Do not download 400KB catalog JSON on homepage idle — only when user opens products/mega menu.
    if (currentPage !== "products" && currentPage !== "categories" && currentPage !== "product") {
      return undefined;
    }
    const w = typeof window !== "undefined" ? window : null;
    if (!w) return undefined;
    const kick = () => warmStaticCatalog();
    const id =
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(kick, { timeout: 4000 })
        : w.setTimeout(kick, 800);
    return () => {
      if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(id);
      else w.clearTimeout(id);
    };
  }, [isAdminRoute, currentPage, warmStaticCatalog]);

  useEffect(() => {
    if (isAdminRoute || !isStaticCatalogOnly()) return;
    if (currentPage !== "products" && currentPage !== "categories") return;
    warmStaticCatalog();
  }, [isAdminRoute, currentPage, warmStaticCatalog]);

  useEffect(() => {
    initManualScrollRestoration();
  }, []);

  /** Sayfa / ürün değişince tepeye sabitle (layout sonrası bir kez daha). */
  useLayoutEffect(() => {
    if (isAdminRoute || typeof window === "undefined") return;
    scrollPageToTop();
  }, [currentPage, selectedProductId, isAdminRoute]);

  /** Mega menüden filtre: zaten `products` sayfasındayken currentPage değişmez — yine tepeye al. */
  useLayoutEffect(() => {
    if (isAdminRoute || typeof window === "undefined") return;
    if (currentPage !== "products" && currentPage !== "categories") return;
    scrollPageToTop();
  }, [categoryFilter, subcategoryFilter, searchQuery, currentPage, isAdminRoute]);

  useEffect(() => {
    if (isAdminRoute) return;
    const previousPage = lastPageRef.current;
    const now = Date.now();
    const durationSec = Math.round((now - pageStartRef.current) / 1000);
    const fromPage = previousPage === currentPage ? "" : previousPage;

    if (cookieConsent) {
      const sessionId = getSessionId();
      const payload = {
        session_id: sessionId,
        page: currentPage,
        from_page: fromPage || null,
        duration_sec: durationSec > 0 ? durationSec : 0,
        referrer: document.referrer || "",
        source: detectSource(document.referrer || ""),
        device_type: getDeviceType(),
        user_agent: navigator.userAgent,
        event_type: "navigation",
      };
      const send = () => {
        fetch(`${API}/analytics/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      };
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(send, { timeout: 4000 });
      } else {
        window.setTimeout(send, 1200);
      }
    }

    lastPageRef.current = currentPage;
    pageStartRef.current = Date.now();
  }, [currentPage, isAdminRoute, cookieConsent]);

  useEffect(() => {
    if (isAdminRoute || !cookieConsent) return;
    const onPageHide = () => {
      const sessionId = getSessionId();
      const durationSec = Math.round((Date.now() - pageStartRef.current) / 1000);
      const payload = {
        session_id: sessionId,
        page: lastPageRef.current || currentPage,
        from_page: null,
        duration_sec: durationSec > 0 ? durationSec : 0,
        referrer: document.referrer || "",
        source: detectSource(document.referrer || ""),
        device_type: getDeviceType(),
        user_agent: navigator.userAgent,
        event_type: "page_exit",
      };
      navigator.sendBeacon?.(`${API}/analytics/track`, new Blob([JSON.stringify(payload)], { type: "application/json" }));
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [currentPage, isAdminRoute, cookieConsent]);

  return (
    <LanguageProvider>
      <CustomerProvider>
      <RFQProvider>
        {isAdminRoute ? (
          <Suspense fallback={<RouteFallback className="min-h-screen" />}>
            <AdminDashboard />
          </Suspense>
        ) : (
        <div
          className={`min-h-dvh min-h-screen w-full max-w-[100vw] bg-[#f8fafc] ${
            currentPage === 'rfq' ? 'overflow-x-visible' : 'overflow-x-clip'
          }`}
          data-testid="app-root"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {!(rfqWorkspaceEmbed && currentPage === 'rfq') ? (
          <>
          <TestSiteBanner visible={SHOW_TEST_SITE_NOTICE} />
          <Header
            onNavigate={handleNavigate}
            currentPage={currentPage}
            onCategorySelect={handleCategorySelect}
            onPrefetchProductCatalog={warmStaticCatalog}
            testSiteBanner={SHOW_TEST_SITE_NOTICE}
          />
          <RouteSeoSync currentPage={currentPage} selectedProductId={selectedProductId} />
          </>
          ) : null}

          <main
            id="main-content"
            className={`relative w-full min-w-0 ${
              currentPage === 'rfq' ? 'overflow-x-visible' : 'overflow-x-clip'
            } ${
              currentPage === 'auth' || currentPage === 'account_dashboard'
                ? 'min-h-dvh flex flex-col'
                : ''
            }`}
          >
            {currentPage === 'home' && (
              <>
                <HeroSection onSearch={handleSearch} onNavigate={handleNavigate} />
                <DeferredSection minHeight={900}>
                  <Suspense fallback={<RouteFallback className="min-h-[900px]" />}>
                    <WorkspaceShowcaseSection />
                  </Suspense>
                </DeferredSection>
                <DeferredSection minHeight={720}>
                  <Suspense fallback={<RouteFallback className="min-h-[720px]" />}>
                    <ProcurementModelsSection />
                  </Suspense>
                </DeferredSection>
                <DeferredSection minHeight={680}>
                  <Suspense fallback={<RouteFallback className="min-h-[680px]" />}>
                    <SmartSourcingShowcase />
                  </Suspense>
                </DeferredSection>
                <DeferredSection minHeight={680}>
                  <Suspense fallback={<RouteFallback className="min-h-[680px]" />}>
                    <MarketInsightsBanner onNavigate={handleNavigate} />
                  </Suspense>
                </DeferredSection>
                <DeferredSection minHeight={420}>
                  <Suspense fallback={<RouteFallback className="min-h-[420px]" />}>
                    <TestimonialsSection />
                  </Suspense>
                </DeferredSection>
                <DeferredSection minHeight={520}>
                  <Suspense fallback={<RouteFallback className="min-h-[520px]" />}>
                    <GlobalDemandSection
                      onProductClick={handleProductClick}
                      onNavigate={handleNavigate}
                      onCategorySelect={handleCategorySelect}
                    />
                  </Suspense>
                </DeferredSection>
                <DeferredSection minHeight={360}>
                  <Suspense fallback={<RouteFallback className="min-h-[360px]" />}>
                    <AboutSection />
                    <HomeBlogSection onNavigate={handleNavigate} />
                    <ContactSection />
                  </Suspense>
                </DeferredSection>
              </>
            )}

            {currentPage === 'products' && (
              <Suspense fallback={<RouteFallback className="min-h-[60vh]" />}>
                <ProductGrid
                  initialCategory={categoryFilter}
                  initialSubcategory={subcategoryFilter}
                  initialSearch={searchQuery}
                  onProductClick={handleProductClick}
                />
              </Suspense>
            )}

            {currentPage === 'product' && selectedProductId && (
              <Suspense fallback={<RouteFallback className="min-h-[60vh]" />}>
                <ProductDetail
                  productId={selectedProductId}
                  onBack={() => handleNavigate('products', undefined, { preserveFilters: true })}
                  onNavigate={handleNavigate}
                  onCategorySelect={handleCategorySelect}
                />
              </Suspense>
            )}

            {currentPage === 'categories' && (
              <Suspense fallback={<RouteFallback className="min-h-[60vh]" />}>
                <ProductGrid
                  initialCategory={categoryFilter}
                  initialSubcategory={subcategoryFilter}
                  initialSearch={searchQuery}
                  onProductClick={handleProductClick}
                />
              </Suspense>
            )}

            {currentPage === 'about' && (
              <Suspense fallback={<RouteFallback className="min-h-[50vh]" />}>
                <AboutSection standalonePage />
              </Suspense>
            )}

            {currentPage === 'contact' && (
              <Suspense fallback={<RouteFallback className="min-h-[50vh]" />}>
                <ContactSection standalonePage />
              </Suspense>
            )}

            {currentPage === 'auth' && (
              <Suspense fallback={<RouteFallback className="min-h-[70vh]" />}>
                <AuthPage onNavigate={handleNavigate} />
              </Suspense>
            )}

            {currentPage === 'account_dashboard' && (
              <Suspense fallback={<RouteFallback className="min-h-[60vh]" />}>
                <AccountDashboardPage onNavigate={handleNavigate} />
              </Suspense>
            )}

            {currentPage === 'blog' && (
              <Suspense fallback={<RouteFallback className="min-h-[60vh]" />}>
                <BlogPage onNavigate={handleNavigate} />
              </Suspense>
            )}

            {currentPage === 'rfq' && (
              <Suspense fallback={<RouteFallback className="min-h-[60vh]" />}>
                <RFQPage onNavigate={handleNavigate} workspaceEmbed={rfqWorkspaceEmbed} />
              </Suspense>
            )}

            {currentPage === 'kvkk' && (
              <Suspense fallback={<RouteFallback className="min-h-[50vh]" />}>
                <KvkkPage onBack={() => handleNavigate('home')} />
              </Suspense>
            )}

            {currentPage === 'gizlilik_politikasi' && (
              <Suspense fallback={<RouteFallback className="min-h-[50vh]" />}>
                <GizlilikPolitikasiPage onBack={() => handleNavigate('home')} />
              </Suspense>
            )}

            {currentPage === 'cerez_politikasi' && (
              <Suspense fallback={<RouteFallback className="min-h-[50vh]" />}>
                <CerezPolitikasiPage onBack={() => handleNavigate('home')} />
              </Suspense>
            )}

          </main>

          {currentPage !== 'auth' && currentPage !== 'account_dashboard' && !(rfqWorkspaceEmbed && currentPage === 'rfq') && (
          <Footer onNavigate={handleNavigate} onCategorySelect={handleCategorySelect} />
          )}

          <CookieConsentBanner
            visible={!cookieConsent}
            onAccept={acceptCookies}
            onOpenCookiePolicy={() => handleNavigate('cerez_politikasi')}
          />

        </div>
        )}
      </RFQProvider>
      </CustomerProvider>
    </LanguageProvider>
  );
}

export default App;
